export type PdfFontFamily = 'times' | 'noto';
export type PdfFontStyle = 'normal' | 'bold';

export interface PdfPageMargins {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** Standard print margins for PDF page content and tables (mm). */
export const PDF_CONTENT_MARGINS: PdfPageMargins = {
  top: 8,
  bottom: 8,
  left: 10,
  right: 10,
};

/** Tighter margins for báo cáo PDF pages. */
export const PDF_BAO_CAO_MARGINS: PdfPageMargins = {
  top: 8,
  bottom: 8,
  left: 8,
  right: 8,
};

export type PdfOrientation = 'portrait' | 'landscape';

export interface PdfPageLayoutOptions {
  orientation?: PdfOrientation;
  margins?: PdfPageMargins;
}

/** Standard font family for PDF document content. */
export const PDF_CONTENT_FONT = {
  family: 'times' as PdfFontFamily,
  cssFamily: "'Times New Roman', Times, serif",
};

/** Standard typography for PDF page overview headers (title / subtitle). */
export const PDF_CONTENT_HEADER = {
  titleFontSize: 14,
  subtitleFontSize: 10,
  titleFontStyle: 'bold' as PdfFontStyle,
  subtitleFontStyle: 'bold' as PdfFontStyle,
  titleSpacing: 7,
  subtitleSpacing: 7,
};

/** Standard typography and layout for PDF tables. */
export const PDF_TABLE_STYLE = {
  headerFontSize: 9,
  headerFontStyle: 'bold' as PdfFontStyle,
  bodyFontSize: 8,
  bodyFontStyle: 'normal' as PdfFontStyle,
  fullWidth: true,
  headerHeight: 8,
  /** Body row line box (mm). Keep close to bodyFontSize so single-line rows stay compact. */
  lineHeight: 3.6,
  /** Horizontal/vertical cell padding (mm) — kept minimal for dense body rows. */
  cellPadding: 0.5,
};

export const PDF_A4 = {
  widthMm: 210,
  heightMm: 297,
  /** A4 width at 96 DPI – used for HTML print templates. */
  widthPx: 794,
};

/** Defaults passed to `generateTablePdf` for standard PDF pages. */
export const PDF_GENERATE_TABLE_DEFAULTS = {
  fontFamily: PDF_CONTENT_FONT.family,
  margins: PDF_CONTENT_MARGINS,
  tableFullWidth: PDF_TABLE_STYLE.fullWidth,
} as const;

export function mmToPx(mm: number, pageWidthPx = PDF_A4.widthPx, pageWidthMm = PDF_A4.widthMm): number {
  return Math.round((mm * pageWidthPx) / pageWidthMm);
}

export function getPdfPageWidthMm(
  options?: PdfPageLayoutOptions,
): number {
  return options?.orientation === 'landscape'
    ? PDF_A4.heightMm
    : PDF_A4.widthMm;
}

export function getPdfPageHeightMm(
  options?: PdfPageLayoutOptions,
): number {
  return options?.orientation === 'landscape'
    ? PDF_A4.widthMm
    : PDF_A4.heightMm;
}

export function getPdfPageWidthPx(options?: PdfPageLayoutOptions): number {
  return options?.orientation === 'landscape'
    ? mmToPx(PDF_A4.heightMm)
    : PDF_A4.widthPx;
}

export function getPdfContentPaddingStyle(
  margins: PdfPageMargins = PDF_CONTENT_MARGINS,
): string {
  const { top, right, bottom, left } = margins;
  return `${mmToPx(top)}px ${mmToPx(right)}px ${mmToPx(bottom)}px ${mmToPx(left)}px`;
}

/** Usable content width (page width minus left/right margins) in mm. */
export function getPdfContentWidthMm(
  options?: PdfPageLayoutOptions,
): number {
  const margins = options?.margins ?? PDF_CONTENT_MARGINS;
  return getPdfPageWidthMm(options) - margins.left - margins.right;
}

/** Usable content width (page width minus left/right margins) in px. */
export function getPdfContentWidthPx(options?: PdfPageLayoutOptions): number {
  const pageWidthMm = getPdfPageWidthMm(options);
  const pageWidthPx = getPdfPageWidthPx(options);
  return mmToPx(getPdfContentWidthMm(options), pageWidthPx, pageWidthMm);
}

/** Inline styles for hidden HTML templates rendered to PDF (html2canvas). */
export function getPdfPrintPaperStyle(): Record<string, string> {
  return {
    width: `${PDF_A4.widthPx}px`,
    padding: getPdfContentPaddingStyle(),
    fontSize: '0.85rem',
    fontFamily: PDF_CONTENT_FONT.cssFamily,
    background: '#fff',
  };
}

/**
 * Inline styles for hidden HTML templates whose page margins are applied during
 * jsPDF placement (image positioned at the page margins). The paper itself is
 * rendered at the usable content width with no padding to avoid double margins.
 */
export function getPdfPrintContentStyle(
  options?: PdfPageLayoutOptions,
): Record<string, string> {
  return {
    width: `${getPdfContentWidthPx(options)}px`,
    fontSize: '0.85rem',
    fontFamily: PDF_CONTENT_FONT.cssFamily,
    // Exposed so descendants can force the PDF font over theme styles (see SCSS).
    '--pdf-content-font': PDF_CONTENT_FONT.cssFamily,
    background: '#fff',
  };
}
